import { Action, IOWidget, mk_pair, mk_widget, Widget } from 'widgets-for-react'
import React from 'react'

import { CMSState, Link } from '../types.gen'
import { link, getClassName } from '../utils.gen'

export type ExpandBlockProps = {
  block_index: number
  title?: string
  intro?: string
  items: ExpandProps[]
  link?: Link
}

export type ExpandProps = { key?: string; title: string; text: string; open: boolean }

export type ExpandBlockStyle = Partial<{
  block_classname: string
  inner_classname: string
  header_classname: string
  title_classname: string
  intro_classname: string
  content_classname: string
  text_classname: string
  footer_classname: string
  clickable_classname: string
  list_classname: string
  expand_item_classname: string
  expand_button_classname: string
  expand_title_classname: string
  expand_icon_classname: string
  expand_text_classname: string
}>

export function ExpandBlock<CustomBlockData, extra_images>(
  props: ExpandBlockProps,
  options?: { style?: ExpandBlockStyle; key?: string }
): IOWidget<CMSState<CustomBlockData, extra_images, never>, Action<CMSState<CustomBlockData, extra_images, never>>> {
  return (s: CMSState<CustomBlockData, extra_images, never>) =>
    mk_widget({
      run: onDone => (
        <section
          className={getClassName(options, 'block_classname', 'container')}
          key={options && options.key ? options.key : 'expand-block'}
        >
          <div className={getClassName(options, 'inner_classname', 'py-5')}>
            {(props.title || props.intro) && (
              <header className={getClassName(options, 'header_classname', '')}>
                {props.title && <h2 className={getClassName(options, 'title_classname', 'mb-3')}>{props.title}</h2>}
                {props.intro && <div className={getClassName(options, 'intro_classname', '')} dangerouslySetInnerHTML={{ __html: props.intro }}></div>}
              </header>
            )}

            <div className={getClassName(options, 'content_classname', '')}>
              <ul className={getClassName(options, 'list_classname', 'accordion list-unstyled mb-3')}>
                {props.items.map((value, index) =>
                  Expand(
                    {
                      key: value.title + `${index}`,
                      text: value.text,
                      open: value.open,
                      title: value.title
                    },
                    options
                  ).run(action => {
                    let openNew = action(value.open)
                    onDone(s1 => {
                      let currentBlock = s1.blocks.get(props.block_index) as any
                      if (currentBlock.kind === 'expandBlock') {
                        currentBlock = {
                          ...currentBlock,
                          value: mk_pair(
                            {
                              ...currentBlock.value.fst,
                              items: currentBlock.value.fst.items.map((e: any, i: number) =>
                                i === index ? { ...e, open: openNew } : e
                              )
                            },
                            currentBlock.value.snd
                          )
                        }
                      }
                      return { ...s1, blocks: s1.blocks.set(props.block_index, currentBlock) }
                    })
                  })
                )}
              </ul>
            </div>

            {props.link && (
              <footer className={getClassName(options, 'footer_classname', '')}>
                {link(
                  props.link,
                  { classname: getClassName(options, 'clickable_classname', 'btn btn-link pl-0') },
                  props.link.title
                )}
              </footer>
            )}
          </div>
        </section>
      )
    })
}

export function Expand(props: ExpandProps, options?: { style?: ExpandBlockStyle; key?: string }): Widget<Action<boolean>> {
  return mk_widget({
    run: onDone => (
      <li className={getClassName(options, 'expand_item_classname', 'card')} key={props.key}>
        <button
          onClick={() => onDone(s => !s)}
          className={getClassName(
            options,
            'expand_button_classname',
            `card-header ${props.open ? 'col--open' : 'col--closed'} text-left border-0`
          )}
          aria-controls={`a11y-${props.key}`}
          aria-expanded={props.open}
        >
          <h3 className={getClassName(options, 'expand_title_classname', 'btn btn-link m-0 pl-0')}>{props.title}</h3>
          <span
            className={getClassName(options, 'expand_icon_classname', 'font-weight-bold mr-2 text-primary')}
            aria-hidden="true"
          >
            {props.open ? '-' : '+'}
          </span>
        </button>

        <div
          id={`a11y-${props.key}`}
          className={getClassName(
            options,
            'expand_text_classname',
            `card-body pb-1 ${props.open ? '' : 'd-none'} text-left border-0`
          )}
          aria-hidden={!props.open}
          dangerouslySetInnerHTML={{ __html: props.text }}
        />
      </li>
    )
  })
}
